/*
 * The one place the application asks whether it may move something.
 *
 * The stylesheet's half of the motion language is `base.css`'s global reduced-motion rule, which
 * zeroes every transition and animation at once. This is the script's half: a scroll that would be
 * smooth, and a state that is only worth showing once it has lasted a while. Both read the same
 * preference and the same tokens, so a reader who asked for less motion gets less of it everywhere
 * and a duration changed in `tokens.css` changes here too.
 */

import { useEffect, useRef, useState } from "react"

/** Whether the reader has asked the system for less motion. False where there is no window. */
export function prefersReducedMotion(): boolean {
	if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * `el.scrollTo` with the behaviour the reader asked for: a caller may ask for `smooth`, and gets
 * `auto` when the reader prefers reduced motion. Everything that scrolls on the reader's behalf
 * goes through here, so the preference is honoured without each caller checking it.
 */
export function scrollTo(el: Element, options: ScrollToOptions): void {
	if (options.behavior === "smooth" && prefersReducedMotion()) {
		el.scrollTo({ ...options, behavior: "auto" })
		return
	}
	el.scrollTo(options)
}

/**
 * A duration token of `tokens.css`, in milliseconds, or the fallback where there is no document to
 * read it from (a test, a server). The fallback is the token's own value, so the two never
 * disagree for long; the token is the one that is designed.
 */
function tokenMs(name: string, fallback: number): number {
	if (typeof document === "undefined") return fallback
	const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
	const match = /^(\d+(?:\.\d+)?)(ms|s)$/.exec(raw)
	if (match === null) return fallback
	return match[2] === "s" ? Number(match[1]) * 1000 : Number(match[1])
}

/**
 * A flag that is false until `active` has held for `delayMs`, then true, and then stays true for
 * at least `holdMs` once it is shown — even when `active` drops at once. It is what keeps a 100 ms
 * load from flashing a skeleton and a 55 ms socket hiccup from flashing the connection banner: a
 * state that is usually invisible earns the screen only by lasting, and once it has the screen it
 * keeps it long enough to be read.
 *
 * The defaults are `--delay-reveal` and `--hold-min`.
 */
export function useDelayedFlag(active: boolean, delayMs?: number, holdMs?: number): boolean {
	const delay = delayMs ?? tokenMs("--delay-reveal", 120)
	const hold = holdMs ?? tokenMs("--hold-min", 320)
	const [shown, setShown] = useState(false)
	// When the flag became true, or null while it is false. A ref rather than state: the effect
	// below reads it to decide what to schedule, and a stale render must not schedule twice.
	const shownAt = useRef<number | null>(null)

	useEffect(() => {
		if (active) {
			if (shownAt.current !== null) return undefined
			const id = setTimeout(() => {
				shownAt.current = Date.now()
				setShown(true)
			}, delay)
			return () => clearTimeout(id)
		}
		if (shownAt.current === null) return undefined
		const remaining = Math.max(0, hold - (Date.now() - shownAt.current))
		const id = setTimeout(() => {
			shownAt.current = null
			setShown(false)
		}, remaining)
		return () => clearTimeout(id)
	}, [active, delay, hold])

	return shown
}
